/**
 * Time zone used for Daily boards and Today leaderboard rankings.
 * America/Chicago represents US Central Time (observing CST/CDT).
 */
export const DAILY_TIME_ZONE = 'America/Chicago';

const dailyPartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: DAILY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const zonedPartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: DAILY_TIME_ZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  hour12: false,
});

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function getZonedParts(date: Date): ZonedParts {
  const parts = zonedPartsFormatter.formatToParts(date);
  let year = 0;
  let month = 0;
  let day = 0;
  let hour = 0;
  let minute = 0;
  let second = 0;
  for (const part of parts) {
    if (part.type === 'year') year = parseInt(part.value, 10);
    else if (part.type === 'month') month = parseInt(part.value, 10);
    else if (part.type === 'day') day = parseInt(part.value, 10);
    else if (part.type === 'hour') hour = parseInt(part.value === '24' ? '0' : part.value, 10);
    else if (part.type === 'minute') minute = parseInt(part.value, 10);
    else if (part.type === 'second') second = parseInt(part.value, 10);
  }
  return { year, month, day, hour, minute, second };
}

/** Format a timestamp as the US Central calendar date (YYYY-MM-DD) used by daily boards/rankings. */
export function dailyDate(timestamp = Date.now()): string {
  const parts = dailyPartsFormatter.formatToParts(new Date(timestamp));
  let year = '';
  let month = '';
  let day = '';
  for (const part of parts) {
    if (part.type === 'year') year = part.value;
    else if (part.type === 'month') month = part.value;
    else if (part.type === 'day') day = part.value;
  }
  return `${year}-${month}-${day}`;
}

/** Validate whether a value is a valid YYYY-MM-DD calendar date string. */
export function isDailyDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/** Returns the epoch millisecond timestamp of the next midnight in Central Time. */
export function nextDailyRolloverMs(timestamp = Date.now()): number {
  const parts = getZonedParts(new Date(timestamp));
  const desiredAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day + 1, 0, 0, 0);
  let targetUtc = desiredAsUtc;
  for (let i = 0; i < 3; i++) {
    const cur = getZonedParts(new Date(targetUtc));
    const curAsUtc = Date.UTC(cur.year, cur.month - 1, cur.day, cur.hour, cur.minute, cur.second);
    const diff = desiredAsUtc - curAsUtc;
    if (diff === 0) break;
    targetUtc += diff;
  }
  return targetUtc;
}
